import { useTranslation } from 'react-i18next'
import { useForm, useFieldArray } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { recipeSchema } from './types'
import { IngredientRow } from './IngredientRow'
import type { RecipeFormData } from './types'

interface RecipeFormProps {
  onSubmit: (data: RecipeFormData) => void
  isPending: boolean
  defaultValues?: Partial<RecipeFormData>
}

const emptyIngredient = {
  ingredientId: null,
  ingredientName: '',
  canonicalUnit: '',
  quantity: 0,
  unit: '',
}

export function RecipeForm({ onSubmit, isPending, defaultValues }: RecipeFormProps) {
  const { t } = useTranslation('recipes')

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RecipeFormData>({
    resolver: zodResolver(recipeSchema) as Resolver<RecipeFormData>,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      name: '',
      servingSize: 4,
      ingredients: [{ ...emptyIngredient }],
      method: null,
      ...defaultValues,
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'ingredients' })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="recipe-name" className="block text-sm font-medium text-ink mb-1">
          {t('form.nameLabel')}
        </label>
        <input
          id="recipe-name"
          type="text"
          {...register('name')}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
        />
        {errors.name && (
          <p role="alert" className="mt-1 text-xs text-danger">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="recipe-servings" className="block text-sm font-medium text-ink mb-1">
          {t('form.servingSizeLabel')}
        </label>
        <input
          id="recipe-servings"
          type="number"
          {...register('servingSize', { valueAsNumber: true })}
          className="w-24 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
        />
        {errors.servingSize && (
          <p role="alert" className="mt-1 text-xs text-danger">{errors.servingSize.message}</p>
        )}
      </div>

      <div>
        <p className="block text-sm font-medium text-ink mb-2">{t('form.ingredientsLabel')}</p>
        {fields.length > 0 && (
          <div className="flex items-start gap-2 mb-1">
            <span className="flex-1 text-xs font-medium text-ink/60 pl-3">{t('form.ingredientColumnHeader')}</span>
            <span className="w-20 text-xs font-medium text-ink/60 pl-2">{t('form.quantityColumnHeader')}</span>
            <span className="w-20 text-xs font-medium text-ink/60 pl-2">{t('form.unitColumnHeader')}</span>
            <span className="min-w-[44px]" aria-hidden="true" />
          </div>
        )}
        <div className="space-y-2">
          {fields.map((field, index) => (
            <IngredientRow
              key={field.id}
              index={index}
              onRemove={remove}
              control={control}
              errors={errors}
            />
          ))}
        </div>
        {errors.ingredients && !Array.isArray(errors.ingredients) && (
          <p role="alert" className="mt-1 text-xs text-danger">{errors.ingredients.message}</p>
        )}
        <button
          type="button"
          onClick={() => append({ ...emptyIngredient })}
          className="mt-2 flex items-center gap-1 text-sm text-accent font-medium min-h-[44px]"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('form.addIngredientButton')}
        </button>
      </div>

      <div>
        <label htmlFor="recipe-method" className="block text-sm font-medium text-ink mb-1">
          {t('form.methodLabel')}
        </label>
        <textarea
          id="recipe-method"
          {...register('method')}
          rows={4}
          placeholder={t('form.methodPlaceholder')}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={isPending}
        className="w-full min-h-[44px] bg-accent text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isPending ? (
          <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" aria-hidden="true" />
        ) : null}
        {t('form.saveButton')}
      </button>
    </form>
  )
}
